import { useRecoilState } from 'recoil'
import axios, { InternalAxiosRequestConfig } from 'axios'
import { anonymousAuthTokenAtom, authTokenAtom, impersonatingAuthTokenAtom } from 'state'
import { LOCAL_STORAGE } from 'app-constants'
import { useCallback, useLayoutEffect, useState } from 'react'
import { oauthGoogle } from 'api/auth_endpoints/authFromFileSpecWithTransformer'
import { LoginStateParam } from 'components/Login'
import { useNavigate } from 'react-router-dom'

// Use a regular expression to extract the URL-encoded part of the string
function extractAndDecodeURL(urlSearchParams: string) {
  // Decode the URL-encoded string
  const decodedString = decodeURIComponent(urlSearchParams)

  // Find the index of 'redirect_path'
  const redirectPathIndex = decodedString.indexOf('redirect_path')

  // If 'redirect_path' is not found, return an empty string or null
  if (redirectPathIndex === -1) {
    return null
  }

  // Find the start of the actual path by looking for the ':' after 'redirect_path'
  const pathStartIndex = decodedString.indexOf(':', redirectPathIndex) + 2 // +2 to account for ':' and the initial quote

  // Extract the substring starting from pathStartIndex
  let redirectPath = decodedString.substring(pathStartIndex)

  // Remove the ending quote and any surrounding characters
  redirectPath = redirectPath.split('"')[0]

  // Decode the inner URL-encoded components of the redirect path
  redirectPath = decodeURIComponent(redirectPath)

  return redirectPath
}

// this hook takes in an authorization code and fetches via google oauth endpoint
function useFetchToken(authCode: string, additionalParamsString: string, scopes?: string) {
  const navigate = useNavigate()

  const [authToken, setToken] = useAuthToken()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchToken = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    // remove the trailing slash from window.location.origin
    const windowLocationHref = window.location.origin.replace(/\/$/, '')

    const oauthGoogleParams = scopes ? { scopes } : undefined

    const redirectPath = encodeURIComponent(window.location.pathname + window.location.search + additionalParamsString)
    const state: LoginStateParam = {
      redirect_path: redirectPath,
    }
    const loginParams: Record<string, string> = {
      state: JSON.stringify(state),
    }
    const urlSearchParams = new URLSearchParams(loginParams)
    try {
      const response = await oauthGoogle(
        {
          code: authCode,
          redirect_uri: windowLocationHref,
        },
        oauthGoogleParams
      )

      setToken(response.data.token)
      const decodedRedirectPath = extractAndDecodeURL(urlSearchParams.toString())

      // on login, the decoded redirect path is null, so we just redirect to the root
      if (!decodedRedirectPath) {
        navigate('/', { replace: true })
      } else {
        navigate(`${decodedRedirectPath}`, { replace: true })
      }
    } catch (e) {
      setError('Failed to fetch jwt token')
    } finally {
      setIsLoading(false)
    }
  }, [authCode, setToken, setIsLoading, setError, scopes, navigate, additionalParamsString])

  useLayoutEffect(() => {
    if (authCode) {
      fetchToken()
    }
  }, [authCode, setToken, fetchToken])

  return {
    authToken,
    isLoading,
    error,
  }
}

function useAuthToken() {
  const [get, set] = useRecoilState(authTokenAtom)

  const setAuthToken = useCallback(
    (token: string | null) => {
      if (token) {
        localStorage.setItem(LOCAL_STORAGE.AUTH_TOKEN, token)
      } else {
        localStorage.removeItem(LOCAL_STORAGE.AUTH_TOKEN)
      }

      set(token)
    },
    [set]
  )

  return [get, setAuthToken] as const
}

function useAnonymousAuthToken() {
  const [get, set] = useRecoilState(anonymousAuthTokenAtom)

  const setAnonymousAuthToken = useCallback(
    (token: string | null) => {
      if (token) {
        localStorage.setItem(LOCAL_STORAGE.ANONYMOUS_AUTH_TOKEN, token)
      } else {
        localStorage.removeItem(LOCAL_STORAGE.ANONYMOUS_AUTH_TOKEN)
      }

      set(token)
    },
    [set]
  )

  return [get, setAnonymousAuthToken] as const
}

function useImpersonatingAuthToken() {
  const [get, set] = useRecoilState(impersonatingAuthTokenAtom)

  const setAuthToken = useCallback(
    (token: string | null) => {
      if (token) {
        localStorage.setItem(LOCAL_STORAGE.IMPERSONATING_AUTH_TOKEN, token)
      } else {
        localStorage.removeItem(LOCAL_STORAGE.IMPERSONATING_AUTH_TOKEN)
      }

      set(token)
    },
    [set]
  )

  return [get, setAuthToken] as const
}

export function getToken() {
  return (
    localStorage.getItem(LOCAL_STORAGE.ANONYMOUS_AUTH_TOKEN) ||
    (localStorage.getItem(LOCAL_STORAGE.IMPERSONATING_AUTH_TOKEN) ?? localStorage.getItem(LOCAL_STORAGE.AUTH_TOKEN))
  )
}

// Setup axios request to add the Authorization header to requests
axios.interceptors.request.use((config) => {
  const token = getToken()

  return {
    ...config,
    headers: token
      ? {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        }
      : config.headers,
  } as InternalAxiosRequestConfig
})

export { useAuthToken, useAnonymousAuthToken, useImpersonatingAuthToken, useFetchToken }
